// Shared in-flight request deduper.
//
// React 18 StrictMode double-invokes effects in development, and several
// layouts (AdminLayout via useBranding) re-render a page ~50ms after mount,
// which fires the same GET twice on every F5. Rather than each page component
// growing its own cache, services wrap their read calls in `dedupeGet` once and
// every screen that calls them inherits the fix.
//
// The cache is keyed by an arbitrary string and holds the *promise*, not the
// resolved value, so concurrent callers share one round trip. Entries are
// short-lived (default 2s) — this collapses duplicate bursts, it is not a data
// cache, so a genuine refetch a moment later still hits the network.
//
// Expired entries are swept on every call rather than merely ignored. Several
// keys are per-row (`getCandidateAuditLog_${id}`), so "ignored but held" grows
// once per candidate a user opens and never shrinks for the life of the tab.
// The sweep is O(entries), and the whole point of the map is that it holds a
// burst rather than a history, so there is nothing here to walk.

const requestCache = new Map();

const evictExpired = (now) => {
  for (const [key, entry] of requestCache) {
    if (now >= entry.expiresAt) requestCache.delete(key);
  }
};

/**
 * Collapse duplicate GETs that fire within `ttlMs` of each other.
 *
 * @param {string} key - Cache key. Must encode every param that changes the
 *   response (e.g. `getCandidates_1_10_search`), or callers will share a
 *   response meant for a different query.
 * @param {() => Promise<any>} fetcher - Runs only on a cache miss.
 * @param {number} [ttlMs=2000] - How long an entry stays shareable.
 * @returns {Promise<any>} The shared promise.
 */
export const dedupeGet = (key, fetcher, ttlMs = 2000) => {
  const now = Date.now();
  evictExpired(now);

  const cached = requestCache.get(key);
  if (cached) {
    return cached.promise;
  }

  // A rejected promise must never be cached — otherwise every caller inside the
  // TTL window inherits the same failure and a retry cannot reach the network.
  const promise = fetcher().catch((error) => {
    requestCache.delete(key);
    throw error;
  });

  requestCache.set(key, { expiresAt: now + ttlMs, promise });
  return promise;
};

/** Drop cached entries so the next call refetches. Omit `key` to clear all. */
export const invalidateDedupe = (key) => {
  if (key === undefined) requestCache.clear();
  else requestCache.delete(key);
};

export default dedupeGet;
